import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowUpIcon, FileClockIcon, ShieldCheckIcon } from 'lucide-react';
import { chatSuggestions } from '../data/portal';
import type { Brand, ChatMessage } from '../types/portal';
interface ChatPanelProps {
  brand: Brand;
  messages: ChatMessage[];
  typing: boolean;
  draft: string;
  onDraftChange: (value: string) => void;
  onSend: (text: string) => void;
}
const ease = [0.23, 1, 0.32, 1] as const;
export function ChatPanel({
  brand,
  messages,
  typing,
  draft,
  onDraftChange,
  onSend
}: ChatPanelProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTo({
      top: el.scrollHeight,
      behavior: 'smooth'
    });
  }, [messages, typing]);
  useEffect(() => {
    if (draft) inputRef.current?.focus();
    // focus only when a prefilled question arrives
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return <section aria-labelledby="chat-title" className="flex h-[calc(100vh-11.5rem)] min-h-[600px] flex-col rounded-2xl border border-slate-200 bg-white shadow-sm" data-id="element-91" data-mp="cvxak1o8krlw">
      <header className="flex items-start justify-between border-b border-slate-100 px-8 py-6" data-id="element-92" data-mp="33rheom90454">
        <div data-id="element-93" data-mp="83467ewojlac">
          <h1 id="chat-title" className="text-xl font-semibold tracking-tight text-slate-900" data-id="element-94" data-mp="82kuwh1jbv5a">
            Ask about your files
          </h1>
          <p className="mt-1 text-sm text-slate-500" data-id="element-95" data-mp="3qbv41wk9ofof">
            Plain-language answers about the buyers you’ve referred.
          </p>
        </div>
        <span className="mt-1 inline-flex items-center gap-2 whitespace-nowrap text-xs text-slate-500" data-id="element-96" data-mp="3gce0bkoksbv3">
          <span className="h-2 w-2 rounded-full bg-accent" aria-hidden="true" data-id="element-97" data-mp="3lb77kvd9r6mt" />
          Synced Oct 21
        </span>
      </header>

      <div ref={scrollRef} className="flex-1 space-y-6 overflow-y-auto px-8 py-8" aria-live="polite" data-id="element-98" data-mp="1ehvdc0cec2ct">
        {messages.map(m => m.role === 'user' ? <motion.div key={m.id} initial={{
        opacity: 0,
        y: 6
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.2,
        ease
      }} className="flex justify-end" data-id="element-99" data-mp="1i2orx3ikxodw">
              <p className="max-w-[70%] rounded-2xl rounded-br-md bg-ink px-4 py-3 text-[15px] leading-relaxed text-white" data-id="element-100" data-mp="14247y40xhmit">
                {m.text}
              </p>
            </motion.div> : <motion.div key={m.id} initial={{
        opacity: 0,
        y: 6
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.22,
        ease
      }} className="flex gap-3" data-id="element-101" data-mp="1vtk2qlcn2fk4">
              <span aria-hidden="true" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-soft text-xs font-bold text-brand" data-id="element-102" data-mp="1n06wyith7jgm">
                {brand.mark}
              </span>
              <div className="max-w-[78%] pt-1" data-id="element-103" data-mp="19rxqclcqple">
                <p className="text-[15px] leading-relaxed text-slate-800" data-id="element-104" data-mp="1b30qa6z3tbg">{m.text}</p>
                {m.source && <span className="mt-2.5 inline-flex items-center gap-1.5 rounded-md bg-slate-100 px-2 py-1 text-xs text-slate-600" data-id="element-105" data-mp="6apfio2g9hmd">
                    <FileClockIcon className="h-3.5 w-3.5 text-slate-400" data-id="element-106" data-mp="f9sxtptslszt" />
                    {m.source}
                  </span>}
              </div>
            </motion.div>)}

        <AnimatePresence data-id="element-107" data-mp="p6nt7zurrd9v">
        {typing && <motion.div key="typing" initial={{
          opacity: 0,
          y: 6
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          transition: {
            duration: 0.12
          }
        }} transition={{
          duration: 0.2,
          ease
        }} className="flex gap-3" aria-label="Looking that up" data-id="element-108" data-mp="p5cpg01bq5ir">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-soft text-xs font-bold text-brand" data-id="element-109" data-mp="17xfb5pstobzv">
              {brand.mark}
            </span>
            <div className="flex items-center gap-1 rounded-2xl bg-slate-100 px-4 py-3" data-id="element-110" data-mp="pc4zq0iy3eja">
              {[0, 1, 2].map(i => <motion.span key={i} className="h-1.5 w-1.5 rounded-full bg-slate-400" animate={{
              opacity: [0.3, 1, 0.3]
            }} transition={{
              duration: 0.9,
              repeat: Infinity,
              delay: i * 0.15,
              ease: 'easeInOut'
            }} data-id="element-111" data-mp="141ayjndvf2qn" />)}
            </div>
          </motion.div>}
        </AnimatePresence>
      </div>

      <div className="border-t border-slate-100 px-8 pb-6 pt-5" data-id="element-112" data-mp="lbl451e0siz9">
        <div className="flex flex-wrap gap-2" aria-label="Suggested questions" data-id="element-113" data-mp="gbgmv95afk7z">
          {chatSuggestions.map(s => <button key={s} onClick={() => onSend(s)} disabled={typing} className="rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-sm text-slate-700 transition-[color,background-color,border-color,box-shadow,transform] duration-200 ease-out hover:-translate-y-px hover:border-accent hover:text-accent-dark hover:shadow-sm active:translate-y-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:opacity-50" data-id="element-114" data-mp="1woxcnpkq5prr">
              {s}
            </button>)}
        </div>

        <form onSubmit={e => {
        e.preventDefault();
        onSend(draft);
      }} className="mt-4 flex items-center gap-2 rounded-xl border border-slate-300 bg-white py-2 pl-4 pr-2 transition-[color,background-color,border-color,box-shadow,transform] duration-200 ease-out focus-within:border-accent focus-within:ring-4 focus-within:ring-accent/15" data-id="element-115" data-mp="2i0wadtw8f5h3">
          <label htmlFor="chat-input" className="sr-only" data-id="element-116" data-mp="30zqnkyg1rxv5">
            Ask a question about a file
          </label>
          <input ref={inputRef} id="chat-input" value={draft} onChange={e => onDraftChange(e.target.value)} placeholder="Ask about a buyer, address or milestone…" autoComplete="off" className="flex-1 bg-transparent py-1.5 text-[15px] text-slate-900 placeholder:text-slate-400 focus:outline-none" data-id="element-117" data-mp="35qgre78trsnq" />
          <button type="submit" aria-label="Send question" disabled={!draft.trim() || typing} className="flex h-10 w-10 items-center justify-center rounded-full bg-brand text-white active:scale-95 transition-[color,background-color,border-color,box-shadow,transform] duration-200 ease-out hover:bg-brand-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:opacity-40" data-id="element-118" data-mp="31mxvkd5mcjoh">
            <ArrowUpIcon className="h-5 w-5" data-id="element-119" data-mp="2itty0gpa5mol" />
          </button>
        </form>

        <p className="mt-3 flex items-center gap-2 text-xs text-slate-500" data-id="element-120" data-mp="2dwbsz1edvrlv">
          <ShieldCheckIcon className="h-4 w-4 shrink-0 text-accent" data-id="element-121" data-mp="28ys7bgfcwcjw" />
          Answers cover milestones, dates and your team. Financial, credit and income details are never shared.
        </p>
      </div>
    </section>;
}